import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Container, Typography, Button, Fab, IconButton, CircularProgress } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ToastContainer, toast } from 'react-toastify';
import DashboardBackButton from "../Components/Common/DashboardBackButton";
import 'react-toastify/dist/ReactToastify.css';
import pattern from "../Assets/Image/patternsvg.svg";
import Carousel from "../Components/Carousel/Carousel.js";
import FinanzaModal from "../Components/Forms/FinanzaModal.js";
import ProjectModal from "../Components/Project/ProjectModal";
import ProjectGallery from "../Components/Common/projectGallery";
import { api } from "../config";

const redGradient = "linear-gradient(90deg, #99191b 0%, #e3764c 100%)";

export default function ProtectedFinanzaDiProgetto() {
    const [allProjects, setAllProjects] = useState([]); // TUTTI i progetti PPP
    const [selectedProjectIds, setSelectedProjectIds] = useState([]); // ID selezionati
    const [carouselProjects, setCarouselProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(""); // "header" | "carousel" | "miniCarousel" | "page"
    const [selectedProject, setSelectedProject] = useState(null);

    const fetchPPPProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch(api("/getFinanzaDiProgetto.php"));
            const data = await response.json();

            if (data.error) {
                console.error("Errore API:", data.message);
                toast.error("Errore nel caricamento dei progetti");
                return;
            }

            // Nuova struttura dati
            const normalized = data.allProjects.map((p) => ({
                ...p,
                titolo: p.titolo || "",
                citta: p.citta || "",
                images: Array.isArray(p.images) ? p.images : [],
                heroImage: p.heroImage || null,
            }));

            setAllProjects(normalized);
            setSelectedProjectIds(data.selectedProjectIds || []);
            setCarouselProjects(normalized.slice(0, 5));

        } catch (e) {
            console.error("Errore caricamento progetti PPP:", e);
            toast.error("Errore di connessione nel caricamento dei progetti");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPPPProjects();
    }, []);

    // Funzione per ottenere i progetti selezionati per la galleria
    const getSelectedProjects = () => {
        if (selectedProjectIds.length === 0) {
            return allProjects; // Se non ci sono selezioni, mostra tutti
        }

        // Filtra i progetti basandosi sugli ID selezionati
        const selected = selectedProjectIds.map(id =>
            allProjects.find(project => project.id === id)
        ).filter(project => project != null); // Rimuovi eventuali undefined

        return selected;
    };

    const openModal = (type) => {
        setModalType(type);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalType("");
    };

    const handleSelectionsUpdate = async (type, selected) => {
        if (type === "carousel") {
            setCarouselProjects(selected);
            toast.success("Carosello principale aggiornato con successo!");
        } else if (type === "miniCarousel") {
            // Per miniCarousel, salva solo gli ID
            const selectedIds = selected.map(project => project.id);
            setSelectedProjectIds(selectedIds);

            // Mostra toast di successo
            toast.success(`Galleria aggiornata! ${selected.length} progetti selezionati`, {
                autoClose: 3000,
                onClose: () => {
                    // Ricarica i dati dopo il toast
                    fetchPPPProjects();
                }
            });
        }
        closeModal();
    };

    // Funzione per forzare il refresh della pagina
    const refreshPage = () => {
        window.location.reload();
    };

    return (
        <>
            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            {/* HEADER */}
            <Box
                sx={{
                    width: "100%",
                    minHeight: { xs: 180, sm: 220 },
                    display: "flex",
                    alignItems: "center",
                    borderTop: "2px solid #fff",
                    borderBottom: "2px solid #fff",
                    background: redGradient,
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <DashboardBackButton
                    boxSx={{ position: 'absolute', left: { xs: 10, sm: 18 }, top: '50%', transform: 'translateY(-50%)', p: 0, zIndex: 10 }}
                    buttonSx={{
                        color: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.26)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
                    }}
                />
                {/* Pattern a sinistra */}
                <Box
                    sx={{
                        flex: "0 0 40%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        height: "100%",
                    }}
                >
                    <Box
                        component="img"
                        src={pattern}
                        alt="pattern"
                        sx={{
                            width: "100%",
                            height: "100%",
                            maxHeight: { xs: 180, sm: 220 },
                            objectFit: "cover",
                            objectPosition: "left center",
                            userSelect: "none",
                            pointerEvents: "none",
                        }}
                    />
                </Box>

                {/* Testo a destra */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        pr: { xs: 2, sm: 6 },
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: 700,
                            fontSize: { xs: "2rem", sm: "clamp(2.5rem, 5vw, 4rem)" },
                            color: "#fff",
                            lineHeight: 1.1,
                            letterSpacing: 0.5,
                            textAlign: "right",
                            m: 0,
                        }}
                    >
                        FINANZA DI PROGETTO
                    </Typography>

                    <Typography
                        sx={{
                            maxWidth: 640,
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "1rem",
                            color: "#fff",
                            opacity: 0.8,
                            lineHeight: 1.5,
                            letterSpacing: 0.3,
                            textAlign: "right",
                        }}
                    >
                        Il Partenariato Pubblico-Privato rappresenta una soluzione strategica per garantire la realizzazione di progetti di alta qualità, riducendo l'impatto economico sul bilancio pubblico e promuovendo lo sviluppo sostenibile delle comunità.
                    </Typography>
                </Box>
            </Box>

            {/* CAROUSEL */}
            <Container
                sx={{
                    mt: 4,
                    maxWidth: "1200px !important",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 2,
                        flexDirection: { xs: "column", sm: "row" },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 300,
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            opacity: 0.8,
                            lineHeight: 1.4,
                            letterSpacing: 0.3,
                            textAlign: "justify",
                            flex: 1,
                        }}
                    >
                        Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.
                        Ut wisi enim ad minim veniam, quis nostrud exerci tation ullamcorper suscipit lobortis nisl ut aliquip ex ea commodo consequat.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        position: "relative",
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                        borderRadius: 1,
                    }}
                >
                    {loading ? (
                        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                            <CircularProgress color="error" />
                        </Box>
                    ) : (
                        <Carousel projects={carouselProjects} />
                    )}
                </Box>
            </Container>

            {/* SEZIONI TESTO */}
            <Container sx={{ mt: 4, maxWidth: "1200px !important", pb: 2 }}>
                <Box sx={{ display: "grid", gap: 4 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#99191b", mb: 1, textAlign: "left" }}>
                            Vantaggi
                        </Typography>
                        <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: "1rem", opacity: 0.8, textAlign: "justify" }}>
                            Grazie all'esperienza dell'ing. Edoardo Ciardiello, specialista in Partenariato Pubblico Privato (PPP) e Project Financing e membro della Commissione PPP del Consiglio Nazionale degli Ingegneri, si occupa dello sviluppo e promozione di progetti tramite gli strumenti del Project Financing e del Leasing in Costruendo. L'azienda offre inoltre supporto tecnico e amministrativo alle Pubbliche Amministrazioni nelle operazioni di Partenariato Pubblico Privato. Esi.Pro partecipa attivamente e organizza convegni per diffondere la conoscenza degli strumenti del PPP, in collaborazione con diversi Ordini degli Ingegneri nelle città di Torino, Milano, Genova, Catania e Napoli.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="h5" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#99191b", mb: 1, textAlign: "left" }}>
                            Ambiti di applicazione
                        </Typography>
                        <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: "1rem", opacity: 0.8, textAlign: "justify" }}>
                            Il PPP è ampiamente utilizzato in vari settori, tra cui Infrastrutture di trasporto (strade, autostrade, ferrovie); Servizi sanitari (ospedali, strutture sanitarie); Energia e ambiente (impianti energetici, progetti di sostenibilità); Tecnologia e innovazione (reti di telecomunicazioni, smart city). Grazie all'esperienza dell'ing. Edoardo Ciardiello, specialista in Partenariato Pubblico Privato (PPP) e Project Financing e membro della Commissione PPP del Consiglio Nazionale degli Ingegneri, si occupa dello sviluppo e promozione di progetti tramite gli strumenti del Project Financing e del Leasing in Costruendo. L'azienda offre inoltre supporto tecnico e amministrativo alle Pubbliche Amministrazioni nelle operazioni di Partenariato Pubblico Privato. Esi.Pro partecipa attivamente e organizza convegni per diffondere la conoscenza degli strumenti del PPP, in collaborazione con diversi Ordini degli Ingegneri nelle città di Torino, Milano, Genova, Catania e Napoli.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="h5" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#99191b", mb: 1, textAlign: "left" }}>
                            Regolamentazione
                        </Typography>
                        <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: "1rem", opacity: 0.8, textAlign: "justify" }}>
                            Il PPP è ampiamente utilizzato in vari settori, tra cui Infrastrutture di trasporto (strade, autostrade, ferrovie); Servizi sanitari (ospedali, strutture sanitarie); Energia e ambiente (impianti energetici, progetti di sostenibilità); Tecnologia e innovazione (reti di telecomunicazioni, smart city). Grazie all'esperienza dell'ing. Edoardo Ciardiello, specialista in Partenariato Pubblico Privato (PPP) e Project Financing e membro della Commissione PPP del Consiglio Nazionale degli Ingegneri, si occupa dello sviluppo e promozione di progetti tramite gli strumenti del Project Financing e del Leasing in Costruendo. L'azienda offre inoltre supporto tecnico e amministrativo alle Pubbliche Amministrazioni nelle operazioni di Partenariato Pubblico Privato. Esi.Pro partecipa attivamente e organizza convegni per diffondere la conoscenza degli strumenti del PPP, in collaborazione con diversi Ordini degli Ingegneri nelle città di Torino, Milano, Genova, Catania e Napoli.
                        </Typography>
                    </Box>
                </Box>
            </Container>

            {/* GALLERIA PROGETTI CON PROJECTGALLERY */}
            <Container sx={{ mt: 6, maxWidth: "1200px !important" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 4,
                        flexDirection: { xs: "column", sm: "row" },
                    }}
                >



                </Box>

                {loading ? (
                    <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                        <CircularProgress color="error" />
                    </Box>
                ) : (
                    <ProjectGallery
                        projects={getSelectedProjects()} // Usa i progetti selezionati
                        onSelect={setSelectedProject}
                    />
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => openModal("miniCarousel")}
                        sx={{
                            bgcolor: "#d32f2f",
                            "&:hover": { bgcolor: "#b71c1c" },
                            textTransform: "none",
                            float: "right", // Cambiato da "left" a "right"
                        }}
                    >
                        Modifica Galleria
                    </Button>


                </Box>
            </Container>

            {/* TESTO FINALE */}
            <Container sx={{ mt: 4, maxWidth: "1200px !important", pb: 2 }}>
                <Box sx={{ display: "grid", gap: 4 }}>
                    <Box>
                        <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: "1rem", opacity: 0.8, textAlign: "justify" }}>
                            Il PPP è ampiamente utilizzato in vari settori, tra cui Infrastrutture di trasporto (strade, autostrade, ferrovie); Servizi sanitari (ospedali, strutture sanitarie); Energia e ambiente (impianti energetici, progetti di sostenibilità); Tecnologia e innovazione (reti di telecomunicazioni, smart city). Grazie all'esperienza dell'ing. Edoardo Ciardiello, specialista in Partenariato Pubblico Privato (PPP) e Project Financing e membro della Commissione PPP del Consiglio Nazionale degli Ingegneri, si occupa dello sviluppo e promozione di progetti tramite gli strumenti del Project Financing e del Leasing in Costruendo. L'azienda offre inoltre supporto tecnico e amministrativo alle Pubbliche Amministrazioni nelle operazioni di Partenariato Pubblico Privato. Esi.Pro partecipa attivamente e organizza convegni per diffondere la conoscenza degli strumenti del PPP, in collaborazione con diversi Ordini degli Ingegneri nelle città di Torino, Milano, Genova, Catania e Napoli.
                        </Typography>
                    </Box>
                </Box>
                <br/>
                <br/>
                <br/>
            </Container>



            {/* MODALS */}
            <AnimatePresence>
                {modalOpen && (
                    <FinanzaModal
                        isOpen={modalOpen}
                        onClose={closeModal}
                        modalType={modalType}
                        allProjects={allProjects} // Passa TUTTI i progetti al modal
                        currentSelections={
                            modalType === "carousel"
                                ? carouselProjects
                                : modalType === "miniCarousel"
                                    ? getSelectedProjects() // Passa i progetti selezionati
                                    : []
                        }
                        onSelectionsUpdate={handleSelectionsUpdate}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {selectedProject && (
                    <ProjectModal
                        project={selectedProject}
                        isOpen={!!selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}